

import { Badge } from '@/src/components/ui/shadcn/badge';
import { Megaphone, BadgeDollarSign, ChevronRight, ToggleLeft, ToggleRight, SearchX, Zap, Handshake } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { ITrafficSource } from '@clickster/constant-definitions/dist-esm/entities/traffic-source/types';
import { IAffiliate } from '@clickster/constant-definitions/dist-esm/entities/affiliate/types';
import { SpotlightSearchMode } from '../../types/template.types';
interface TabItem {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  className?: string;
}
import CampaignRender from '../campaign-render';
import QuickActionsRender from '../quick-actions/render';
import { accountSettings } from '../more-options/account-settings';
import { addResources } from '../more-options/add-resources';
import { integrations } from '../more-options/integrations';

interface Offer {
  _id?: string;
  name: string;
  status?: string;
  url?: string;
  affiliate?: {
    name?: string;
  };
}

interface SearchResultsProps {
  searchQuery: string;
  campaigns: any[];
  campaignFilter: 'active' | 'archived' | 'all';
  handleCampaignFilterClick: () => void;
  isFetchingCampaigns: boolean;
  tabItems: TabItem[];
  onClose: () => void;
  searchMode: SpotlightSearchMode;
  setSearchMode: (mode: SpotlightSearchMode) => void;
  filteredOffers: Offer[];
  isFetchingOffers: boolean;
  offerFilter: 'active' | 'archived' | 'all';
  handleOfferFilterClick: () => void;
  filteredTrafficSources: ITrafficSource[];
  isFetchingTrafficSources: boolean;
  trafficSourceFilter: 'active' | 'inactive' | 'all';
  handleTrafficSourceFilterClick: () => void;
  filteredNetworks: IAffiliate[];
  isFetchingNetworks: boolean;
  networkFilter: 'active' | 'inactive' | 'all';
  handleNetworkFilterClick: () => void;
  collapsedNetworks?: Record<string, boolean>;
  setCollapsedNetworks?: (value: React.SetStateAction<Record<string, boolean>>) => void;
  quickActions: any[];
  campaignManagementOptions: any[];
  isSearching?: boolean;
  filterItems: (items: any[], query: string) => any[];
}

export const SearchResults = ({
  searchQuery,
  campaigns,
  campaignFilter,
  handleCampaignFilterClick,
  isFetchingCampaigns,
  tabItems,
  onClose,
  searchMode,
  setSearchMode,
  filteredOffers,
  isFetchingOffers,
  offerFilter,
  handleOfferFilterClick,
  filteredTrafficSources,
  isFetchingTrafficSources,
  trafficSourceFilter,
  handleTrafficSourceFilterClick,
  filteredNetworks,
  isFetchingNetworks,
  networkFilter,
  handleNetworkFilterClick,
  collapsedNetworks,
  setCollapsedNetworks,
  filterItems,
  quickActions,
  campaignManagementOptions,
  isSearching = false,
}: SearchResultsProps) => {
  const router = useRouter();

  // Combine all searchable actions from different sections
  const allSearchableActions = useMemo(() => {
    return [
      ...quickActions,
      ...campaignManagementOptions,
      ...accountSettings.filter(item => !item.disabled),
      ...addResources,
      ...integrations,
    ];
  }, [quickActions, campaignManagementOptions]);

  // Filter all actions based on search query
  const filteredAllActions = useMemo(() => {
    return filterItems(allSearchableActions, searchQuery);
  }, [allSearchableActions, searchQuery, filterItems]);

  // Check if there are any results at all
  const hasAnyResults = useMemo(() => {
    if (searchMode === 'campaigns') {
      return campaigns.length > 0 || filteredAllActions.length > 0;
    }
    if (searchMode === 'traffic-sources') {
      return filteredTrafficSources.length > 0;
    }
    if (searchMode === 'networks') {
      return filteredNetworks.length > 0;
    }
    return filteredOffers.length > 0;
  }, [
    searchMode,
    campaigns.length,
    filteredOffers.length,
    filteredTrafficSources.length,
    filteredNetworks.length,
    filteredAllActions.length,
  ]);

  if (!searchQuery) return null;

  return (
    <div className="space-y-6" data-testid="spotlight-search-results">
      {/* Mode Toggle */}
      <div data-testid="spotlight-search-mode-toggle" className="flex items-center justify-center gap-2 py-2">
        <Badge
          data-testid="spotlight-search-mode-campaigns"
          variant="outline"
          className={`cursor-pointer transition-all duration-200 ease-in-out relative ${searchMode === 'campaigns'
            ? 'text-white before:absolute before:inset-0 before:rounded-md before:bg-gradient-to-r before:from-purple-500/90 before:to-blue-500/90 before:-z-10'
            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          onClick={() => setSearchMode('campaigns')}
        >
          <div className="relative z-10 flex items-center">
            <Megaphone className="w-3 h-3 mr-1" />
            Campaigns
          </div>
        </Badge>
        <Badge
          data-testid="spotlight-search-mode-offers"
          variant="outline"
          className={`cursor-pointer transition-all duration-200 ease-in-out relative ${searchMode === 'offers'
            ? 'text-white before:absolute before:inset-0 before:rounded-md before:bg-gradient-to-r before:from-purple-500/90 before:to-blue-500/90 before:-z-10'
            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          onClick={() => setSearchMode('offers')}
        >
          <div className="relative z-10 flex items-center">
            <BadgeDollarSign className="w-3 h-3 mr-1" />
            Offers
          </div>
        </Badge>
        <Badge
          data-testid="spotlight-search-mode-traffic-sources"
          variant="outline"
          className={`cursor-pointer transition-all duration-200 ease-in-out relative ${searchMode === 'traffic-sources'
            ? 'text-white before:absolute before:inset-0 before:rounded-md before:bg-gradient-to-r before:from-purple-500/90 before:to-blue-500/90 before:-z-10'
            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          onClick={() => setSearchMode('traffic-sources')}
        >
          <div className="relative z-10 flex items-center">
            <Zap className="w-3 h-3 mr-1" />
            Traffic Sources
          </div>
        </Badge>
        <Badge
          data-testid="spotlight-search-mode-networks"
          variant="outline"
          className={`cursor-pointer transition-all duration-200 ease-in-out relative ${searchMode === 'networks'
            ? 'text-white before:absolute before:inset-0 before:rounded-md before:bg-gradient-to-r before:from-purple-500/90 before:to-blue-500/90 before:-z-10'
            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          onClick={() => setSearchMode('networks')}
        >
          <div className="relative z-10 flex items-center">
            <Handshake className="w-3 h-3 mr-1" />
            Networks
          </div>
        </Badge>
      </div>

      {searchMode === 'campaigns' && (
        <div className="space-y-3">
          <CampaignRender
            searchQuery={searchQuery}
            campaigns={campaigns}
            tabItems={tabItems}
            onClose={onClose}
            campaignFilter={campaignFilter}
            handleCampaignFilterClick={handleCampaignFilterClick}
            isFetchingCampaigns={isFetchingCampaigns}
          />
        </div>
      )}

      {searchMode === 'offers' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 pb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Offers</h3>
            <button
              onClick={handleOfferFilterClick}
              className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors cursor-pointer inline-flex items-center gap-1.5
                ${offerFilter === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                  : offerFilter === 'archived'
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700/50'
                }`}
              data-testid="spotlight-offer-filter-button"
            >
              {offerFilter === 'active'
                ? 'Displaying active offers'
                : offerFilter === 'archived'
                  ? 'Displaying archived offers'
                  : 'Displaying all offers'}
              {offerFilter === 'active' ? (
                <ToggleRight className="w-3 h-3" />
              ) : offerFilter === 'archived' ? (
                <ToggleLeft className="w-3 h-3" />
              ) : (
                <div className="flex items-center gap-0.5">
                  <ToggleRight className="w-3 h-3 text-green-600 dark:text-green-400" />
                  <ToggleLeft className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                </div>
              )}
            </button>
            <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
          </div>

          {isFetchingOffers && (
            <p className="p-4 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="spotlight-offers-searching">
              Searching offers…
            </p>
          )}

          {!isFetchingOffers && filteredOffers.length === 0 && (
            <p className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">No offers found</p>
          )}

          {!isFetchingOffers && filteredOffers.length > 0 && (() => {
            const groupedOffers = filteredOffers.reduce((acc, offer) => {
              const category = offer.affiliate?.name || 'Other';
              if (!acc[category]) {
                acc[category] = [];
              }
              acc[category].push(offer);
              return acc;
            }, {} as Record<string, any[]>);

            return (
              <div className="mt-3 overflow-y-auto max-h-[450px] rounded-lg border border-gray-200 dark:border-gray-700 [scrollbar-width:thin]" style={{
                scrollbarWidth: 'thin',
              }}>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(groupedOffers).map(([category, offers]) => {
                    const isCollapsed = collapsedNetworks?.[category] || false;

                    return (
                      <div key={category}>
                        <div 
                          className="flex items-center gap-2 cursor-pointer p-2 bg-gray-50 dark:bg-gray-800/50" 
                          onClick={() => setCollapsedNetworks?.({ 
                            ...collapsedNetworks, 
                            [category]: !isCollapsed 
                          })}
                          data-testid={`spotlight-offer-category-${category}`}
                        >
                          <ChevronRight className={`w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
                          <h4 className="text-sm font-medium flex-1">{category}</h4>
                          <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400 px-2 py-0.5 rounded">
                            {offers.length}
                          </span>
                        </div>

                        {!isCollapsed && (
                          <div>
                            {offers.map((offer, index) => (
                              <div
                                key={offer._id || index}
                                className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                onClick={() => {
                                  window.open(`/dashboard/offers?id=${offer._id}`, '_blank');
                                  onClose();
                                }}
                                data-testid={`spotlight-offer-item-${offer._id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 mt-0"></span>
                                    <div>
                                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{offer.name}</h3>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 max-w-[200px] truncate">
                                        {offer.url}
                                      </p>
                                    </div>
                                  </div>
                                  <div>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                                      ${offer.status === 'active' ?
                                        'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400' :
                                        'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'}`}>
                                      {offer.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {searchMode === 'traffic-sources' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 pb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Traffic Sources</h3>
            <button
              onClick={handleTrafficSourceFilterClick}
              className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors cursor-pointer inline-flex items-center gap-1.5
                ${trafficSourceFilter === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                  : trafficSourceFilter === 'inactive'
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700/50'
                }`}
              data-testid="spotlight-traffic-source-filter-button"
            >
              {trafficSourceFilter === 'active'
                ? 'Displaying active'
                : trafficSourceFilter === 'inactive'
                  ? 'Displaying inactive'
                  : 'Displaying all'}
              {trafficSourceFilter === 'active' ? (
                <ToggleRight className="w-3 h-3" />
              ) : trafficSourceFilter === 'inactive' ? (
                <ToggleLeft className="w-3 h-3" />
              ) : (
                <div className="flex items-center gap-0.5">
                  <ToggleRight className="w-3 h-3 text-green-600 dark:text-green-400" />
                  <ToggleLeft className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                </div>
              )}
            </button>
            <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
          </div>

          {isFetchingTrafficSources && (
            <p
              className="p-4 text-center text-sm text-gray-500 dark:text-gray-400"
              data-testid="spotlight-traffic-sources-searching"
            >
              Searching traffic sources…
            </p>
          )}

          {!isFetchingTrafficSources && filteredTrafficSources.length === 0 && (
            <p className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">No traffic sources found</p>
          )}

          {!isFetchingTrafficSources && filteredTrafficSources.length > 0 && (
            <div
              className="mt-3 overflow-y-auto max-h-[450px] rounded-lg border border-gray-200 dark:border-gray-700 [scrollbar-width:thin]"
              style={{ scrollbarWidth: 'thin' }}
            >
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTrafficSources.map((source) => (
                  <div
                    key={source._id}
                    className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    onClick={() => {
                      window.open(`/dashboard/traffic-sources?id=${source._id}`, '_blank');
                      onClose();
                    }}
                    data-testid={`spotlight-traffic-source-item-${source._id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {source.logo ? (
                          <img
                            src={source.logo}
                            alt={source.name}
                            className="w-6 h-6 rounded object-contain"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300">
                            {source.name?.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{source.name}</h3>
                          {source.nickname && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{source.nickname}</p>
                          )}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                          ${source.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'
                          }`}
                      >
                        {source.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {searchMode === 'networks' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 pb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Affiliate Networks</h3>
            <button
              onClick={handleNetworkFilterClick}
              className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors cursor-pointer inline-flex items-center gap-1.5
                ${networkFilter === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                  : networkFilter === 'inactive'
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700/50'
                }`}
              data-testid="spotlight-network-filter-button"
            >
              {networkFilter === 'active'
                ? 'Displaying active'
                : networkFilter === 'inactive'
                  ? 'Displaying inactive'
                  : 'Displaying all'}
              {networkFilter === 'active' ? (
                <ToggleRight className="w-3 h-3" />
              ) : networkFilter === 'inactive' ? (
                <ToggleLeft className="w-3 h-3" />
              ) : (
                <div className="flex items-center gap-0.5">
                  <ToggleRight className="w-3 h-3 text-green-600 dark:text-green-400" />
                  <ToggleLeft className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                </div>
              )}
            </button>
            <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
          </div>

          {isFetchingNetworks && (
            <p
              className="p-4 text-center text-sm text-gray-500 dark:text-gray-400"
              data-testid="spotlight-networks-searching"
            >
              Searching networks…
            </p>
          )}

          {!isFetchingNetworks && filteredNetworks.length === 0 && (
            <p className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">No networks found</p>
          )}

          {!isFetchingNetworks && filteredNetworks.length > 0 && (
            <div
              className="mt-3 overflow-y-auto max-h-[450px] rounded-lg border border-gray-200 dark:border-gray-700 [scrollbar-width:thin]"
              style={{ scrollbarWidth: 'thin' }}
            >
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredNetworks.map((network) => (
                  <div
                    key={network._id}
                    className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    onClick={() => {
                      window.open(`/networks?id=${network._id}`, '_blank');
                      onClose();
                    }}
                    data-testid={`spotlight-network-item-${network._id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300">
                          {network.name?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{network.name}</h3>
                          {network.api_configuration?.platform && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {network.api_configuration.platform}
                            </p>
                          )}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                          ${network.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'
                          }`}
                      >
                        {network.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions Results - Show all filtered actions from all sections */}
      {searchMode === 'campaigns' && !isFetchingCampaigns && !isSearching && filteredAllActions.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Quick Actions</h3>
          <QuickActionsRender
            actions={filteredAllActions}
            onActionClick={(action) => {
              if (action.disabled) return;
              
              // Handle template-based actions
              if (action.template) {
                return;
              }
              
              // Handle onClick actions
              if (action.onClick) {
                action.onClick();
                onClose();
                return;
              }
              
              // Handle link-based actions
              if (action.link) {
                router.push(action.link);
                onClose();
              }
              
              if (action.href) {
                router.push(action.href);
                onClose();
              }
            }}
          />
        </div>
      )}

      {/* No Results Message */}
      {!isSearching && !isFetchingCampaigns && !isFetchingOffers && !isFetchingTrafficSources && !isFetchingNetworks && !hasAnyResults && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <SearchX className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No results found
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
            We couldn&apos;t find anything matching &quot;{searchQuery}&quot;. Try adjusting your search terms or browse the options below.
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
